import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  Tabs,
  Tab,
  InputAdornment,
} from '@mui/material';
import { Add, Search, PictureAsPdf } from '@mui/icons-material';
import { Abastecimento, Veiculo, SolicitacaoAbastecimento, PostoCombustivel, Motorista } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { abastecimentoService } from '../services/abastecimentoService';
import { veiculoService } from '../services/veiculoService';
import { solicitacaoService } from '../services/solicitacaoService';
import { postoService } from '../services/postoService';
import { precoCombustivelService } from '../services/precoCombustivelService';
import { motoristaService } from '../services/motoristaService';
import { getErrorMessage } from '../utils/errors';
import { formatarChaveNfe, somenteDigitos } from '../utils/mascara';
import VeiculoPickerDialog from '../components/VeiculoPickerDialog';
import DateMaskField from '../components/DateMaskField';
import PdfViewerDialog from '../components/PdfViewerDialog';

const calcularValorTotal = (litros?: number, valorUnitario?: number) =>
  Math.round((litros || 0) * (valorUnitario || 0) * 100) / 100;

const formatarDecimal = (valor: number | undefined, casas: number) =>
  (valor ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });

const MAX_LITROS_DIGITOS = 7;
const MAX_VALOR_DIGITOS = 10;

const formatarDigitos = (digitos: string, casas: number): string => {
  const d = digitos.replace(/\D/g, '');
  const inteiro = d.length > casas ? d.slice(0, d.length - casas) : '';
  const fracao = d.length > casas ? d.slice(-casas) : d.padStart(casas, '0');
  const inteiroAgrupado = (inteiro || '0').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${inteiroAgrupado},${fracao}`;
};

const numeroDeDigitos = (digitos: string, casas: number): number => {
  const d = digitos.replace(/\D/g, '');
  return d ? Number(d) / 10 ** casas : 0;
};

const digitosDeNumero = (valor: number | undefined, casas: number): string =>
  Math.max(0, valor ?? 0).toFixed(casas).replace(/\D/g, '').replace(/^0+/, '');

const limitarDigitos = (texto: string, maxDigitos: number): string =>
  texto.replace(/\D/g, '').replace(/^0+/, '').slice(-maxDigitos);

const textoInserido = (antigo: string, novo: string): string => {
  let inicio = 0;
  while (inicio < antigo.length && inicio < novo.length && antigo[inicio] === novo[inicio]) inicio++;
  let fimA = antigo.length;
  let fimN = novo.length;
  while (fimA > inicio && fimN > inicio && antigo[fimA - 1] === novo[fimN - 1]) {
    fimA--;
    fimN--;
  }
  return novo.slice(inicio, fimN);
};

const numeroDeTexto = (texto: string): number => {
  const t = (texto || '').replace(/[^\d.,]/g, '');
  if (!t) return NaN;
  if (t.includes(',')) {
    const [inteiro, fracao, ...demais] = t.split(',');
    if (demais.length > 0) return NaN;
    return Number(`${inteiro.replace(/\./g, '')}.${fracao ?? ''}`);
  }
  return /^\d{1,3}(\.\d{3})+$/.test(t) ? Number(t.replace(/\./g, '')) : Number(t);
};


const Abastecimentos: React.FC = () => {
  const [abastecimentos, setAbastecimentos] = useState<Abastecimento[]>([]);
  const [solicitacoesDisponiveis, setSolicitacoesDisponiveis] = useState<SolicitacaoAbastecimento[]>([]);
  const [postos, setPostos] = useState<PostoCombustivel[]>([]);
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [open, setOpen] = useState(false);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const { hasRole } = useAuth();
  const isGestor = hasRole('ADMIN') || hasRole('GERENTE');

  const postoPadrao = postos.length === 1 ? postos[0].nome : '';

  const abastecimentoVazio = (): Abastecimento => ({
    veiculoId: 0,
    motoristaId: undefined,
    combustivel: '', 
    litrosAbastecidos: 0,
    valorTotal: 0,
    valorUnitario: 0,
    quilometragemNoMomento: 0,
    postoCombustivel: postoPadrao,
    numeroNotaFiscal: '',
    serieNotaFiscal: '',
    chaveNfe: '',
    dataAbastecimento: undefined,
  });

  const [novoAbastecimento, setNovoAbastecimento] = useState<Abastecimento>(abastecimentoVazio());
  const [solicitacaoSelecionada, setSolicitacaoSelecionada] = useState<number | null>(null);
  const [motoristasDoVeiculo, setMotoristasDoVeiculo] = useState<Motorista[]>([]);
  const [modoDireto, setModoDireto] = useState(false);
  const [veiculoPickerOpen, setVeiculoPickerOpen] = useState(false);
  const [valorUnitarioEditando, setValorUnitarioEditando] = useState(false);
  const [litrosEditando, setLitrosEditando] = useState(false);
  const [litrosTexto, setLitrosTexto] = useState('0,000');
  const [valorUnitarioTexto, setValorUnitarioTexto] = useState('0,00');
  const [veiculoTipoCombustivel, setVeiculoTipoCombustivel] = useState('');
  const [pdfViewer, setPdfViewer] = useState<{ url: string; title: string } | null>(null);
  const [filtro, setFiltro] = useState('');

  const resetarMascaras = () => {
    setLitrosEditando(false);
    setValorUnitarioEditando(false);
    setLitrosTexto('0,000');
    setValorUnitarioTexto('0,00');
  };

  useEffect(() => {
    if (!litrosEditando) {
      setLitrosTexto(formatarDigitos(digitosDeNumero(novoAbastecimento.litrosAbastecidos, 3), 3));
    }
  }, [novoAbastecimento.litrosAbastecidos, litrosEditando]);

  useEffect(() => {
    if (!valorUnitarioEditando) {
      setValorUnitarioTexto(formatarDigitos(digitosDeNumero(novoAbastecimento.valorUnitario, 2), 2));
    }
  }, [novoAbastecimento.valorUnitario, valorUnitarioEditando]);

  useEffect(() => {
    loadDados();
  }, []);

  useEffect(() => {
    if (postos.length === 1) {
      setNovoAbastecimento((prev) => prev.postoCombustivel ? prev : { ...prev, postoCombustivel: postos[0].nome });
    }
  }, [postos]);

  const loadDados = async () => {
    try {
      const [abasts, solsAprovadas, postosData] = await Promise.all([
        abastecimentoService.listar(),
        solicitacaoService.listarPorStatus('APROVADA'),
        postoService.listarAtivos(),
      ]);

      setAbastecimentos(abasts);
      setPostos(postosData);

      const solicitacoesComAbastecimento = new Set(
        abasts.filter(a => a.solicitacaoId).map(a => a.solicitacaoId)
      );
      const disponiveis = solsAprovadas.filter(s => !solicitacoesComAbastecimento.has(s.id!));
      setSolicitacoesDisponiveis(disponiveis);
    } catch (error) {
      console.error('Erro ao carregar dados', error);
    }
  };

  const carregarVeiculos = async () => {
    try {
      const [lista, motoristas] = await Promise.all([
        veiculoService.listar(),
        motoristaService.listar(),
      ]);
      const veiculosComMotorista = new Set<number>();
      motoristas
        .filter(m => m.ativo)
        .forEach(m => m.veiculosAlocados?.forEach(v => veiculosComMotorista.add(v.id)));
      setVeiculos(lista.filter(v => v.status === 'ATIVO' && v.id && veiculosComMotorista.has(v.id)));
    } catch (error) {
      console.error('Erro ao carregar veículos', error);
    }
  };

  const abrirDialogo = async () => {
    setErro('');
    setSolicitacaoSelecionada(null);
    setMotoristasDoVeiculo([]);
    setVeiculoTipoCombustivel('');
    setNovoAbastecimento(abastecimentoVazio());
    resetarMascaras();
    setModoDireto(isGestor && solicitacoesDisponiveis.length === 0);
    if (isGestor) carregarVeiculos();
    try {
      const postosData = await postoService.listarAtivos();
      setPostos(postosData);
      setNovoAbastecimento((prev) => ({
        ...prev,
        postoCombustivel: prev.postoCombustivel || (postosData.length === 1 ? postosData[0].nome : prev.postoCombustivel),
      }));
    } catch (error) {
      console.error('Erro ao carregar postos', error);
    }
    setOpen(true);
  };

  const fecharDialogo = () => {
    setOpen(false);
    resetarMascaras();
  };

  const mudarModo = (direto: boolean) => {
    setModoDireto(direto);
    setErro('');
    setSolicitacaoSelecionada(null);
    setMotoristasDoVeiculo([]);
    setVeiculoTipoCombustivel('');
    setNovoAbastecimento(abastecimentoVazio());
    resetarMascaras();
  };

  const handleRegistrar = async () => {
    if (modoDireto) {
      if (!novoAbastecimento.veiculoId || !novoAbastecimento.motoristaId) {
        setErro('Selecione o veículo e o motorista para registrar o abastecimento');
        return;
      }
    } else if (!solicitacaoSelecionada) {
      setErro('Selecione uma solicitação aprovada para registrar o abastecimento');
      return;
    }
    setSalvando(true);
    try {
      await abastecimentoService.registrar(novoAbastecimento);
      loadDados();
      fecharDialogo();
      setNovoAbastecimento(abastecimentoVazio());
      setSolicitacaoSelecionada(null);
      setMotoristasDoVeiculo([]);
      setModoDireto(false);
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  const handleSolicitacaoSelect = async (solId: number) => {
    setSolicitacaoSelecionada(solId);
    const sol = solicitacoesDisponiveis.find(s => s.id === solId);
    if (!sol) return;

    let tipoCombustivel = veiculos.find(v => v.id === sol.veiculoId)?.tipoCombustivel;
    if (!tipoCombustivel) {
      try {
        const v = await veiculoService.buscarPorId(sol.veiculoId);
        tipoCombustivel = v.tipoCombustivel;
      } catch (error) {
        console.error('Erro ao carregar veículo', error);
      }
    }
    setVeiculoTipoCombustivel(tipoCombustivel || '');

    let motoristas: Motorista[] = [];
    try {
      motoristas = await motoristaService.listarPorVeiculo(sol.veiculoId);
    } catch (error) {
      console.error('Erro ao carregar motoristas do veículo', error);
    }
    setMotoristasDoVeiculo(motoristas);

    const escolhido = motoristas.some(m => m.id === sol.motoristaId)
      ? sol.motoristaId
      : motoristas[0]?.id;

    const litros = sol.litrosSolicitados ?? 0;
    const combustivel = combustivelPadrao(tipoCombustivel);
    let valorUnitario = litros > 0
      ? Math.round(((sol.valorEstimado ?? 0) / litros) * 100) / 100
      : 0;
    try {
      const resp = await precoCombustivelService.ultimo(combustivel as any, novoAbastecimento.postoCombustivel);
      const preco = Number(resp.data);
      if (Number.isFinite(preco) && preco > 0) valorUnitario = preco;
    } catch (error) {
      console.error('Erro ao carregar preço do combustível', error);
    }

    setNovoAbastecimento({
      ...novoAbastecimento,
      solicitacaoId: solId,
      veiculoId: sol.veiculoId,
      motoristaId: escolhido,
      combustivel,
      litrosAbastecidos: litros,
      valorUnitario,
      valorTotal: calcularValorTotal(litros, valorUnitario),
      quilometragemNoMomento: sol.quilometragemAtual,
    });
  };

  const handleVeiculoDiretoSelect = async (veiculoId: number) => {
    const sol = solicitacoesDisponiveis.find(s => s.veiculoId === veiculoId);
    const veiculo = veiculos.find(v => v.id === veiculoId);
    const tipoCombustivel = veiculo?.tipoCombustivel;
    setVeiculoTipoCombustivel(tipoCombustivel || '');

    let motoristas: Motorista[] = [];
    try {
      motoristas = await motoristaService.listarPorVeiculo(veiculoId);
    } catch (error) {
      console.error('Erro ao carregar motoristas do veículo', error);
    }
    setMotoristasDoVeiculo(motoristas);

    const escolhido = sol && motoristas.some(m => m.id === sol.motoristaId)
      ? sol.motoristaId
      : motoristas[0]?.id;

    const litros = sol?.litrosSolicitados ?? 0;
    const combustivel = combustivelPadrao(tipoCombustivel);
    let valorUnitario = litros > 0
      ? Math.round(((sol?.valorEstimado ?? 0) / litros) * 100) / 100
      : 0;
    try {
      const resp = await precoCombustivelService.ultimo(combustivel as any, novoAbastecimento.postoCombustivel);
      const preco = Number(resp.data);
      if (Number.isFinite(preco) && preco > 0) valorUnitario = preco;
    } catch (error) {
      console.error('Erro ao carregar preço do combustível', error);
    }

    setNovoAbastecimento({
      ...abastecimentoVazio(),
      veiculoId,
      motoristaId: escolhido,
      combustivel,
      litrosAbastecidos: litros,
      valorUnitario,
      valorTotal: calcularValorTotal(litros, valorUnitario),
      quilometragemNoMomento: sol?.quilometragemAtual ?? 0,
    });
  };

  const motoristaSelecionado = solicitacoesDisponiveis.find(s => s.id === solicitacaoSelecionada);
  const veiculoSelecionado = veiculos.find(v => v.id === novoAbastecimento.veiculoId);
  const veiculoSelecionadoLabel = veiculoSelecionado
    ? `${veiculoSelecionado.placa} - ${veiculoSelecionado.marca} ${veiculoSelecionado.modelo}`
    : '';
  const solicitacaoVinculada = novoAbastecimento.veiculoId
    ? solicitacoesDisponiveis.find(s => s.veiculoId === novoAbastecimento.veiculoId)
    : undefined;

  const opcoesCombustivel = (tipo?: string): string[] => {
    switch (tipo) {
      case 'FLEX': return ['GASOLINA', 'ETANOL'];
      case 'DIESEL': return ['DIESEL_S10', 'DIESEL_COMUM'];
      case 'GASOLINA': return ['GASOLINA'];
      case 'ETANOL': return ['ETANOL'];
      case 'GNV': return ['GNV'];
      default: return [];
    }
  };
  const labelCombustivel = (codigo: string) => {
    switch (codigo) {
      case 'DIESEL_S10': return 'Diesel S10';
      case 'DIESEL_COMUM': return 'Diesel Comum';
      case 'GASOLINA': return 'Gasolina';
      case 'ETANOL': return 'Etanol';
      case 'GNV': return 'GNV';
      default: return codigo;
    }
  };
  const combustivelPadrao = (tipo?: string) => opcoesCombustivel(tipo)[0] || '';

  const abrirPdf = (blob: Blob, titulo: string) => {
    const url = window.URL.createObjectURL(blob);
    setPdfViewer((prev) => {
      if (prev) window.URL.revokeObjectURL(prev.url);
      return { url, title: titulo };
    });
  };

  const fecharPdfViewer = () => {
    setPdfViewer((prev) => {
      if (prev) window.URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  const visualizarGuia = async (id?: number) => {
    if (!id) return;
    try {
      const blob = await abastecimentoService.gerarGuiaPdf(id);
      abrirPdf(blob, `Guia de Abastecimento ${id}`);
    } catch (error) {
      setErro(getErrorMessage(error));
    }
  };

  const podeRegistrar = modoDireto
    ? !!novoAbastecimento.veiculoId && !!novoAbastecimento.motoristaId && !!novoAbastecimento.postoCombustivel && !!novoAbastecimento.combustivel
    : !!solicitacaoSelecionada && !!novoAbastecimento.postoCombustivel && !!novoAbastecimento.combustivel;

  const termo = filtro.trim().toLowerCase();
  const abastecimentosFiltrados = termo
    ? abastecimentos.filter((item) => Object.values(item).filter(Boolean).some((campo) => String(campo).toLowerCase().includes(termo)))
    : abastecimentos;

  return (
    <Container maxWidth={false} disableGutters sx={{ minWidth: 0, width: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Abastecimentos</Typography>
        <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          {(hasRole('ADMIN') || hasRole('GERENTE') || hasRole('MOTORISTA')) && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={abrirDialogo}
            >
              Registrar Abastecimento
            </Button>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Veículo</TableCell>
              <TableCell>Motorista</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Litros</TableCell>
              <TableCell>Valor Unit.</TableCell>
              <TableCell>Valor Total</TableCell>
              <TableCell>Km</TableCell>
              <TableCell>Posto</TableCell>
              <TableCell>Combustível</TableCell>
              <TableCell>NF</TableCell>
              <TableCell>Série</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {abastecimentosFiltrados.map((abast) => (
              <TableRow key={abast.id}>
                <TableCell>{abast.id}</TableCell>
                <TableCell>{abast.veiculoPlaca}</TableCell>
                <TableCell>{abast.motoristaNome}</TableCell>
                <TableCell>{abast.dataAbastecimento ? new Date(abast.dataAbastecimento).toLocaleString('pt-BR') : '-'}</TableCell>
                <TableCell>{formatarDecimal(abast.litrosAbastecidos, 3)} L</TableCell>
                <TableCell>
                  {abast.valorUnitario != null
                    ? `R$ ${abast.valorUnitario.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                    : '-'}
                </TableCell>
                <TableCell>R$ {abast.valorTotal?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</TableCell>
                <TableCell>{abast.quilometragemNoMomento?.toLocaleString('pt-BR')} km</TableCell>
                <TableCell>{abast.postoCombustivel}</TableCell>
                <TableCell>{abast.combustivel ? labelCombustivel(abast.combustivel) : '-'}</TableCell>
                <TableCell>{abast.numeroNotaFiscal || '-'}</TableCell>
                <TableCell>{abast.serieNotaFiscal || '-'}</TableCell>
                <TableCell>
                  <Typography variant="caption" color={(abast as any).situacaoPagamento === 'EMISSAO_DE_NOTA' ? 'warning.main' : 'success.main'}>
                    {(abast as any).situacaoPagamento === 'EMISSAO_DE_NOTA' ? 'EMISSÃO DE NOTA' : 'ABERTO'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Button size="small" startIcon={<PictureAsPdf />} onClick={() => visualizarGuia(abast.id)}>
                    Guia
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={fecharDialogo} maxWidth="sm" fullWidth>
        <DialogTitle>Registrar Abastecimento</DialogTitle>
        <DialogContent>
          {isGestor && (
            <Tabs
              value={modoDireto ? 'direto' : 'solicitacao'}
              onChange={(_, value) => mudarModo(value === 'direto')}
              sx={{ mb: 1 }}
            >
              <Tab label="Com solicitação" value="solicitacao" />
              <Tab label="Direto (sem solicitação)" value="direto" />
            </Tabs>
          )}

          {!modoDireto ? (
            <>
              <TextField
                fullWidth
                select
                label="Veículo (Solicitação Aprovada)"
                value={solicitacaoSelecionada || ''}
                onChange={(e) => handleSolicitacaoSelect(Number(e.target.value))}
                margin="normal"
                required
              >
                <MenuItem value="">Selecione um veículo...</MenuItem>
                {solicitacoesDisponiveis.map((sol) => (
                  <MenuItem key={sol.id} value={sol.id}>
                    {sol.veiculoPlaca} - {sol.litrosSolicitados}L - {sol.motoristaNome}
                  </MenuItem>
                ))}
              </TextField>

              {solicitacoesDisponiveis.length === 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Nenhuma solicitação aprovada pendente de abastecimento.
                </Alert>
              )}

              {solicitacaoSelecionada && (motoristasDoVeiculo.length > 0 ? (
                <TextField
                  fullWidth
                  select
                  label="Motorista que abasteceu"
                  value={novoAbastecimento.motoristaId ?? ''}
                  onChange={(e) => setNovoAbastecimento({ ...novoAbastecimento, motoristaId: Number(e.target.value) })}
                  margin="normal"
                  required
                  helperText="Selecione, entre os motoristas vinculados ao veículo, quem realizou o abastecimento."
                >
                  {motoristasDoVeiculo.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.nome}
                      {m.id === motoristaSelecionado?.motoristaId ? ' (solicitante)' : ''}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  fullWidth
                  label="Motorista que abasteceu"
                  value={motoristaSelecionado?.motoristaNome || ''}
                  margin="normal"
                  disabled
                  helperText="Nenhum motorista vinculado ao veículo; será usado o motorista da solicitação."
                />
              ))}
            </>
          ) : (
            <>
              <TextField
                fullWidth
                label="Veículo"
                value={veiculoSelecionadoLabel}
                onClick={() => setVeiculoPickerOpen(true)}
                margin="normal"
                required
                placeholder="Selecione um veículo..."
                inputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ cursor: 'pointer', '& .MuiInputBase-input': { cursor: 'pointer' } }}
              />

              {veiculos.length === 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Nenhum veículo ativo com motorista vinculado disponível.
                </Alert>
              )}

              {novoAbastecimento.veiculoId > 0 && (
                motoristasDoVeiculo.length > 0 ? (
                  <TextField
                    fullWidth
                    select
                    label="Motorista que abasteceu"
                    value={novoAbastecimento.motoristaId ?? ''}
                    onChange={(e) => setNovoAbastecimento({ ...novoAbastecimento, motoristaId: Number(e.target.value) })}
                    margin="normal"
                    required
                    helperText="Selecione, entre os motoristas vinculados ao veículo, quem realizou o abastecimento."
                  >
                    {motoristasDoVeiculo.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.nome}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Nenhum motorista vinculado a este veículo. Vincule um motorista antes de registrar.
                  </Alert>
                )
              )}

              {solicitacaoVinculada && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Este veículo tem solicitação aprovada de {solicitacaoVinculada.motoristaNome || 'um motorista'}; ela será vinculada a este abastecimento.
                </Alert>
              )}

              <DateMaskField
                label="Data e hora do abastecimento"
                comHora
                value={novoAbastecimento.dataAbastecimento || ''}
                onChange={(v) => setNovoAbastecimento({ ...novoAbastecimento, dataAbastecimento: v || undefined })}
                helperText="Deixe em branco para usar a data/hora atual."
              />
            </>
          )}

          <TextField
            fullWidth
            select
            label="Posto de Combustível"
            value={novoAbastecimento.postoCombustivel}
            onChange={(e) => setNovoAbastecimento({ ...novoAbastecimento, postoCombustivel: e.target.value })}
            margin="normal"
            required
          >
            <MenuItem value="">Selecione um posto...</MenuItem>
            {postos.map((posto) => (
              <MenuItem key={posto.id} value={posto.nome}>
                {posto.nome}
              </MenuItem>
            ))}
          </TextField>

          {postos.length === 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Nenhum posto de combustível ativo disponível.
            </Alert>
          )}

          <TextField
            fullWidth
            select
            label="Combustível"
            value={novoAbastecimento.combustivel || ''}
            onChange={async (e) => {
              const codigo = e.target.value;
              setNovoAbastecimento((prev) => ({ ...prev, combustivel: codigo }));
              try {
                const resp = await precoCombustivelService.ultimo(codigo as any, novoAbastecimento.postoCombustivel);
                const preco = Number(resp.data);
                if (Number.isFinite(preco) && preco > 0) {
                  setNovoAbastecimento((prev) => ({
                    ...prev,
                    combustivel: codigo,
                    valorUnitario: preco,
                    valorTotal: calcularValorTotal(prev.litrosAbastecidos, preco),
                  }));
                }
              } catch (error) {
                console.error('Erro ao carregar preço do combustível', error);
              }
            }}
            margin="normal"
            required
            helperText="Combustível efetivamente abastecido."
          >
            <MenuItem value="">Selecione o combustível...</MenuItem>
            {opcoesCombustivel(veiculoTipoCombustivel || veiculoSelecionado?.tipoCombustivel).map((codigo) => (
              <MenuItem key={codigo} value={codigo}>
                {labelCombustivel(codigo)}
              </MenuItem>
            ))}
          </TextField>

          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              label="Litros Abastecidos"
              value={litrosTexto}
              onFocus={(e) => {
                setLitrosEditando(true);
                const el = e.currentTarget;
                el.setSelectionRange(el.value.length, el.value.length);
              }}
              onBlur={() => setLitrosEditando(false)}
              onChange={(e) => {
                const bruto = e.target.value;
                const inserido = textoInserido(litrosTexto, bruto);
                const colado = /[.,]/.test(inserido) && /\d/.test(inserido)
                  ? numeroDeTexto(inserido)
                  : NaN;
                const digitos = Number.isFinite(colado)
                  ? limitarDigitos(digitosDeNumero(colado, 3), MAX_LITROS_DIGITOS)
                  : limitarDigitos(bruto, MAX_LITROS_DIGITOS);
                const texto = formatarDigitos(digitos, 3);
                if (texto !== bruto) e.target.value = texto;
                setLitrosTexto(texto);
                const litros = numeroDeDigitos(digitos, 3);
                setNovoAbastecimento({
                  ...novoAbastecimento,
                  litrosAbastecidos: litros,
                  valorTotal: calcularValorTotal(litros, novoAbastecimento.valorUnitario),
                });
              }}
              margin="normal"
              inputProps={{ inputMode: 'numeric' }}
            />
            <TextField
              fullWidth
              label="Valor Unitário"
              value={valorUnitarioTexto}
              onFocus={(e) => {
                setValorUnitarioEditando(true);
                const el = e.currentTarget;
                el.setSelectionRange(el.value.length, el.value.length);
              }}
              onBlur={() => setValorUnitarioEditando(false)}
              onChange={(e) => {
                const bruto = e.target.value;
                const inserido = textoInserido(valorUnitarioTexto, bruto);
                const colado = /[.,]/.test(inserido) && /\d/.test(inserido)
                  ? numeroDeTexto(inserido)
                  : NaN;
                const digitos = Number.isFinite(colado)
                  ? limitarDigitos(digitosDeNumero(colado, 2), MAX_VALOR_DIGITOS)
                  : limitarDigitos(bruto, MAX_VALOR_DIGITOS);
                const texto = formatarDigitos(digitos, 2);
                if (texto !== bruto) e.target.value = texto;
                setValorUnitarioTexto(texto);
                const valorUnitario = numeroDeDigitos(digitos, 2);
                setNovoAbastecimento({
                  ...novoAbastecimento,
                  valorUnitario,
                  valorTotal: calcularValorTotal(novoAbastecimento.litrosAbastecidos, valorUnitario),
                });
              }}
              margin="normal"
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              inputProps={{ inputMode: 'numeric' }}
              helperText="Preço por litro"
            />
            <TextField
              fullWidth
              label="Valor Total"
              value={formatarDecimal(novoAbastecimento.valorTotal, 2)}
              margin="normal"
              InputProps={{ readOnly: true, startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              inputProps={{ tabIndex: -1 }}
              helperText="Litros × valor unitário"
            />
          </Box>
          <Box display="flex" gap={2}>
            <TextField
              label="Quilometragem no Momento"
              type="number"
              value={novoAbastecimento.quilometragemNoMomento}
              onChange={(e) => setNovoAbastecimento({ ...novoAbastecimento, quilometragemNoMomento: Number(e.target.value) })}
              margin="normal"
              sx={{ flex: 50 }}
            />
            <TextField
              label="Nº da Nota Fiscal"
              value={novoAbastecimento.numeroNotaFiscal}
              onChange={(e) => setNovoAbastecimento({ ...novoAbastecimento, numeroNotaFiscal: e.target.value })}
              margin="normal"
              inputProps={{ maxLength: 30 }}
              sx={{ flex: 40 }}
            />
            <TextField
              label="Série"
              value={novoAbastecimento.serieNotaFiscal}
              onChange={(e) => setNovoAbastecimento({ ...novoAbastecimento, serieNotaFiscal: e.target.value })}
              margin="normal"
              inputProps={{ maxLength: 10 }}
              sx={{ flex: 30 }}
            />
          </Box>
          <TextField
            fullWidth
            label="Chave da NF-e"
            value={formatarChaveNfe(novoAbastecimento.chaveNfe)}
            onChange={(e) =>
              setNovoAbastecimento({ ...novoAbastecimento, chaveNfe: somenteDigitos(e.target.value).slice(0, 44) })
            }
            margin="normal"
            inputProps={{ maxLength: 54, inputMode: 'numeric' }}
            helperText={`${(novoAbastecimento.chaveNfe || '').length}/44 dígitos`}
            error={(novoAbastecimento.chaveNfe || '').length > 0 && (novoAbastecimento.chaveNfe || '').length !== 44}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialogo}>Cancelar</Button>
          <Button onClick={handleRegistrar} variant="contained" disabled={!podeRegistrar || salvando}>
            {salvando ? 'Registrando...' : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!erro} onClose={() => setErro('')} maxWidth="xs" fullWidth>
        <DialogTitle>Erro</DialogTitle>
        <DialogContent>
          <Alert severity="error">{erro}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErro('')} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>

      <PdfViewerDialog
        open={!!pdfViewer}
        url={pdfViewer?.url ?? null}
        title={pdfViewer?.title}
        onClose={fecharPdfViewer}
      />

      <VeiculoPickerDialog
        open={veiculoPickerOpen}
        veiculos={veiculos}
        selectedId={novoAbastecimento.veiculoId}
        onClose={() => setVeiculoPickerOpen(false)}
        onSelect={(v) => {
          handleVeiculoDiretoSelect(v.id as number);
          setVeiculoPickerOpen(false);
        }}
      />
    </Container>
  );
};

export default Abastecimentos;
